import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import * as coreEntities from 'core/store/index';

import analyticsService from '../../../../services/analyticsService';

const PAGE_SIZE = 10;

type TopicItem = {
  disciplineId: string;
  disciplineName: string;
  frequency: number;
  topic: string;
  topicId: string | null;
};

type TopicRankingResponse = {
  endDate: string;
  startDate: string;
  topics: Array<{
    disciplineId: string;
    frequency: number;
    topic: string;
    topicId: string | null;
  }>;
};

type UseTopicRankingSectionProps = {
  allCourseIds: string[];
  classValue: string;
  groupCourseMap: Record<string, string[]>;
  timePeriodValue: string;
};

type UseTopicRankingSectionReturn = {
  currentCourseIds: string[];
  currentEndDate: string | undefined;
  currentStartDate: string | undefined;
  disciplineFilter: string;
  disciplineOptions: Array<{ label: string; value: string }>;
  error: string | null;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  setDisciplineFilter: (value: string) => void;
  setSortBy: (value: string) => void;
  sortBy: string;
  totalFrequency: number;
  visibleTopics: TopicItem[];
};

function getDateRange(timePeriodValue: string): { startDate: string | undefined; endDate: string | undefined } {
  const now = new Date();
  const endDate = now.toISOString();
  let startDate: Date;

  switch (timePeriodValue) {
    case '7d':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30d':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case '3m':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 3);
      break;
    default:
      return { startDate: undefined, endDate: undefined };
  }

  return { startDate: startDate.toISOString(), endDate };
}

function useTopicRankingSection({ allCourseIds, classValue, groupCourseMap, timePeriodValue }: UseTopicRankingSectionProps): UseTopicRankingSectionReturn {
  const [allTopics, setAllTopics] = useState<TopicItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [disciplineFilter, setDisciplineFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('popularity');
  const [hasMore, setHasMore] = useState<boolean>(false);
  const offsetRef = useRef<number>(0);
  const disciplines = useSelector((state: any) => coreEntities.educationLevels.selectors.getDisciplines(state)) || [];

  const disciplineMap: Record<string, string> = useMemo(() => {
    if (!disciplines || disciplines.length === 0) return {};
    const map: Record<string, string> = {};
    disciplines.forEach((d: any) => {
      if (d.guid && d.discipline) {
        map[d.guid] = d.discipline;
      }
    });
    return map;
  }, [disciplines]);

  const currentCourseIds = useMemo(() => {
    return classValue === 'all' ? allCourseIds : groupCourseMap[classValue] || [];
  }, [classValue, allCourseIds, groupCourseMap]);

  const { startDate: currentStartDate, endDate: currentEndDate } = useMemo(() => {
    return getDateRange(timePeriodValue);
  }, [timePeriodValue]);

  const enrichTopics = useCallback(
    (rawTopics: TopicRankingResponse['topics']): TopicItem[] => {
      return rawTopics.map((t) => ({
        disciplineId: t.disciplineId,
        disciplineName: disciplineMap[t.disciplineId] || t.disciplineId || '',
        frequency: t.frequency,
        topic: t.topic,
        topicId: t.topicId,
      }));
    },
    [disciplineMap]
  );

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    setError(null);
    offsetRef.current = 0;

    try {
      const data: TopicRankingResponse = await analyticsService.getTopicRanking({
        courseIds: currentCourseIds,
        startDate: currentStartDate,
        endDate: currentEndDate,
        limit: PAGE_SIZE,
        offset: 0,
      });

      const topics = data.topics || [];
      setAllTopics(enrichTopics(topics));
      setHasMore(topics.length >= PAGE_SIZE);
      offsetRef.current = topics.length;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setAllTopics([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [currentCourseIds, currentStartDate, currentEndDate, enrichTopics]);

  useEffect(() => {
    if (allCourseIds.length > 0) {
      fetchTopics();
    } else {
      setLoading(false);
      setAllTopics([]);
      setHasMore(false);
    }
  }, [fetchTopics, allCourseIds.length]);

  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    setLoadingMore(true);

    try {
      const data: TopicRankingResponse = await analyticsService.getTopicRanking({
        courseIds: currentCourseIds,
        startDate: currentStartDate,
        endDate: currentEndDate,
        limit: PAGE_SIZE,
        offset: offsetRef.current,
      });

      const topics = data.topics || [];
      const enriched = enrichTopics(topics);
      setAllTopics((prev) => [...prev, ...enriched]);
      setHasMore(topics.length >= PAGE_SIZE);
      offsetRef.current += topics.length;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [currentCourseIds, currentStartDate, currentEndDate, enrichTopics, loadingMore]);

  const filteredTopics = useMemo(() => {
    if (disciplineFilter === 'all') return allTopics;
    return allTopics.filter((t) => t.disciplineId === disciplineFilter);
  }, [allTopics, disciplineFilter]);

  const sortedTopics = useMemo(() => {
    const sorted = [...filteredTopics];
    if (sortBy === 'popularity') {
      sorted.sort((a, b) => b.frequency - a.frequency);
    } else if (sortBy === 'alphabetical') {
      sorted.sort((a, b) => a.topic.localeCompare(b.topic));
    }
    return sorted;
  }, [filteredTopics, sortBy]);

  const totalFrequency = useMemo(() => {
    return allTopics.reduce((sum, t) => sum + t.frequency, 0);
  }, [allTopics]);

  const disciplineOptions = useMemo(() => {
    const uniqueDisciplines = new Map<string, string>();
    allTopics.forEach((t) => {
      if (t.disciplineId && t.disciplineId !== 'null') {
        uniqueDisciplines.set(t.disciplineId, t.disciplineName);
      }
    });
    return Array.from(uniqueDisciplines.entries()).map(([id, name]) => ({
      label: name,
      value: id,
    }));
  }, [allTopics]);

  return {
    currentCourseIds,
    currentEndDate,
    currentStartDate,
    disciplineFilter,
    disciplineOptions,
    error,
    hasMore,
    loading,
    loadingMore,
    loadMore,
    setDisciplineFilter,
    setSortBy,
    sortBy,
    totalFrequency,
    visibleTopics: sortedTopics,
  };
}

export default useTopicRankingSection;
export type { TopicItem };
